/**
 * WelcomeSection Component
 * Dashboard welcome section with hero text and infinite-loop carousel.
 *
 * Carousel behaviour:
 *  - Infinite loop: scrolling past card 6 wraps to card 1 and vice-versa
 *  - Auto-advances every 10 s
 *  - Pauses on hover, drag, arrow/dot click, focus-within, and hidden tab
 *  - Resumes 8 s after last interaction
 *  - Drag-to-scroll with pointer events (mouse + touch)
 *  - Respects prefers-reduced-motion (disables auto-play)
 *
 * Implementation: the track renders  [clone-tail] [real cards] [clone-head]
 * and silently repositions once a scroll settles inside a clone zone.
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  MapPinned,
  Users,
  PackageCheck,
  Radio,
  MousePointerClick,
  Route,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAnalysisDialogStore } from '@/stores/analysisDialogStore';
import { cn } from '@/lib/utils';

// ─── data ────────────────────────────────────────────────────

export interface WelcomeSectionProps {
  className?: string;
}

interface ProcessStepData {
  titleKey: string;
  descriptionKey: string;
  icon: React.ReactNode;
}

const processSteps: ProcessStepData[] = [
  { titleKey: 'welcome.step1Title', descriptionKey: 'welcome.step1Description', icon: <MapPinned className="w-5 h-5" /> },
  { titleKey: 'welcome.step2Title', descriptionKey: 'welcome.step2Description', icon: <Users className="w-5 h-5" /> },
  { titleKey: 'welcome.step3Title', descriptionKey: 'welcome.step3Description', icon: <PackageCheck className="w-5 h-5" /> },
  { titleKey: 'welcome.step4Title', descriptionKey: 'welcome.step4Description', icon: <Radio className="w-5 h-5" /> },
  { titleKey: 'welcome.step5Title', descriptionKey: 'welcome.step5Description', icon: <MousePointerClick className="w-5 h-5" /> },
  { titleKey: 'welcome.step6Title', descriptionKey: 'welcome.step6Description', icon: <Route className="w-5 h-5" /> },
];

const TOTAL = processSteps.length;
const CLONES = 3; // cloned cards on each side for seamless wrap

/** Build extended array: [last CLONES] + [all real] + [first CLONES] */
interface ExtendedCard extends ProcessStepData {
  realIndex: number; // 0-based index into processSteps
}
const extendedCards: ExtendedCard[] = [
  ...processSteps.slice(-CLONES).map((s, i) => ({ ...s, realIndex: TOTAL - CLONES + i })),
  ...processSteps.map((s, i) => ({ ...s, realIndex: i })),
  ...processSteps.slice(0, CLONES).map((s, i) => ({ ...s, realIndex: i })),
];

/** Map any extended index back to a real 0–(TOTAL-1) index */
const toRealIndex = (extIdx: number) => ((extIdx - CLONES) % TOTAL + TOTAL) % TOTAL;

// ─── constants ───────────────────────────────────────────────

const AUTO_PLAY_MS = 10000;
const RESUME_AFTER_MS = 8000;
const DRAG_CLICK_THRESHOLD = 6;
const SETTLE_MS = 120; // time after last scroll event before we consider it "settled"

// ─── component ───────────────────────────────────────────────

export function WelcomeSection({ className }: WelcomeSectionProps) {
  const { t } = useTranslation();
  const openAnalysisDialog = useAnalysisDialogStore((s) => s.open);

  // refs
  const trackRef = useRef<HTMLDivElement>(null);
  const autoTimerRef = useRef<ReturnType<typeof setInterval>>();
  const resumeTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const settleTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const isPausedRef = useRef(false);
  const prefersReducedMotion = useRef(false);
  const isRepositioning = useRef(false);

  // drag refs
  const dragging = useRef(false);
  const dragStartX = useRef(0);
  const dragStartY = useRef(0);
  const scrollAtDragStart = useRef(0);
  const dragDirectionDecided = useRef(false);
  const dragIsHorizontal = useRef(false);
  const dragDistance = useRef(0);

  // state
  const [activeIndex, setActiveIndex] = useState(0);
  const [isDragCursor, setIsDragCursor] = useState(false);

  // ─── helpers (all positions read from actual DOM) ──────────

  /** scrollLeft value that centres the given card in the track viewport */
  const scrollLeftForCard = useCallback((extIdx: number) => {
    const el = trackRef.current;
    if (!el) return 0;
    const card = el.children[extIdx] as HTMLElement | undefined;
    if (!card) return 0;
    return card.offsetLeft + card.offsetWidth / 2 - el.clientWidth / 2;
  }, []);

  /** Extended index of the card whose centre is closest to the scroll centre */
  const currentExtIndex = useCallback(() => {
    const el = trackRef.current;
    if (!el) return CLONES;
    const scrollCenter = el.scrollLeft + el.clientWidth / 2;
    let closest = CLONES;
    let minDist = Infinity;
    for (let i = 0; i < el.children.length; i++) {
      const child = el.children[i] as HTMLElement;
      const dist = Math.abs(child.offsetLeft + child.offsetWidth / 2 - scrollCenter);
      if (dist < minDist) { minDist = dist; closest = i; }
    }
    return closest;
  }, []);

  /** Instantly (no animation) jump to centre the given card */
  const jumpTo = useCallback(
    (extIdx: number) => {
      const el = trackRef.current;
      if (!el) return;
      isRepositioning.current = true;
      el.style.scrollSnapType = 'none';
      el.scrollLeft = scrollLeftForCard(extIdx);
      requestAnimationFrame(() => {
        el.style.scrollSnapType = '';
        isRepositioning.current = false;
      });
    },
    [scrollLeftForCard],
  );

  /** Smooth-scroll to centre the given card */
  const smoothScrollTo = useCallback(
    (extIdx: number) => {
      trackRef.current?.scrollTo({ left: scrollLeftForCard(extIdx), behavior: 'smooth' });
    },
    [scrollLeftForCard],
  );

  /** After scrolling settles: reposition out of clone zone, then snap to centre */
  const settleAndSnap = useCallback(() => {
    let ext = currentExtIndex();

    // If in a clone zone, silently jump to the matching real card first
    if (ext < CLONES) {
      ext = ext + TOTAL;
      jumpTo(ext);
    } else if (ext >= CLONES + TOTAL) {
      ext = ext - TOTAL;
      jumpTo(ext);
    }

    // Snap the nearest card to centre (handles native touch scroll on mobile)
    smoothScrollTo(ext);
  }, [currentExtIndex, jumpTo, smoothScrollTo]);

  // ─── scroll state bookkeeping ─────────────────────────────

  const onScroll = useCallback(() => {
    if (isRepositioning.current) return;
    setActiveIndex(toRealIndex(currentExtIndex()));

    // After scrolling settles, reposition clones + snap card to center
    clearTimeout(settleTimerRef.current);
    settleTimerRef.current = setTimeout(settleAndSnap, SETTLE_MS);
  }, [currentExtIndex, settleAndSnap]);

  // ─── auto-play ─────────────────────────────────────────────

  const scrollToNext = useCallback(() => {
    smoothScrollTo(currentExtIndex() + 1);
  }, [smoothScrollTo, currentExtIndex]);

  const stopAutoPlay = useCallback(() => {
    clearInterval(autoTimerRef.current);
    autoTimerRef.current = undefined;
  }, []);

  const startAutoPlay = useCallback(() => {
    if (prefersReducedMotion.current) return;
    stopAutoPlay();
    autoTimerRef.current = setInterval(() => {
      if (!isPausedRef.current) scrollToNext();
    }, AUTO_PLAY_MS);
  }, [scrollToNext, stopAutoPlay]);

  const pauseAutoPlay = useCallback(() => {
    isPausedRef.current = true;
    clearTimeout(resumeTimerRef.current);
  }, []);

  const scheduleResume = useCallback(() => {
    clearTimeout(resumeTimerRef.current);
    resumeTimerRef.current = setTimeout(() => {
      isPausedRef.current = false;
    }, RESUME_AFTER_MS);
  }, []);

  const interactionPause = useCallback(() => {
    pauseAutoPlay();
    scheduleResume();
  }, [pauseAutoPlay, scheduleResume]);

  // ─── lifecycle ─────────────────────────────────────────────

  // Set initial scroll to real card 0 before first paint
  useLayoutEffect(() => {
    jumpTo(CLONES);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // reduced motion
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    prefersReducedMotion.current = mq.matches;
    const onMqChange = (e: MediaQueryListEvent) => {
      prefersReducedMotion.current = e.matches;
      if (e.matches) stopAutoPlay();
      else startAutoPlay();
    };
    mq.addEventListener('change', onMqChange);

    // scroll
    const el = trackRef.current;
    el?.addEventListener('scroll', onScroll, { passive: true });

    // keep position on resize (stay on same real card)
    const onResize = () => {
      const real = activeIndex;
      jumpTo(real + CLONES);
    };
    window.addEventListener('resize', onResize);

    // visibility
    const onVisibility = () => {
      if (document.hidden) pauseAutoPlay();
      else {
        isPausedRef.current = false;
        startAutoPlay();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    startAutoPlay();

    return () => {
      mq.removeEventListener('change', onMqChange);
      el?.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      document.removeEventListener('visibilitychange', onVisibility);
      stopAutoPlay();
      clearTimeout(resumeTimerRef.current);
      clearTimeout(settleTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── pointer (drag) handlers ───────────────────────────────

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.button !== 0) return;
      dragging.current = true;
      dragStartX.current = e.clientX;
      dragStartY.current = e.clientY;
      scrollAtDragStart.current = trackRef.current?.scrollLeft ?? 0;
      dragDirectionDecided.current = false;
      dragIsHorizontal.current = false;
      dragDistance.current = 0;
      pauseAutoPlay();
    },
    [pauseAutoPlay],
  );

  const onPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging.current) return;
    const dx = e.clientX - dragStartX.current;
    const dy = e.clientY - dragStartY.current;

    if (!dragDirectionDecided.current) {
      if (Math.abs(dx) > DRAG_CLICK_THRESHOLD || Math.abs(dy) > DRAG_CLICK_THRESHOLD) {
        dragDirectionDecided.current = true;
        dragIsHorizontal.current = Math.abs(dx) >= Math.abs(dy);
        if (dragIsHorizontal.current) {
          trackRef.current?.setPointerCapture(e.pointerId);
          setIsDragCursor(true);
        }
      }
      return;
    }

    if (!dragIsHorizontal.current) return;

    e.preventDefault();
    dragDistance.current = Math.abs(dx);
    const el = trackRef.current;
    if (el) el.scrollLeft = scrollAtDragStart.current - dx;
  }, []);

  const onPointerUp = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!dragging.current) return;
      dragging.current = false;
      setIsDragCursor(false);

      if (dragIsHorizontal.current) {
        trackRef.current?.releasePointerCapture(e.pointerId);
        // Snap to nearest card using actual DOM position
        smoothScrollTo(currentExtIndex());
      }

      scheduleResume();
    },
    [currentExtIndex, smoothScrollTo, scheduleResume],
  );

  const onClickCapture = useCallback((e: React.MouseEvent) => {
    if (dragDistance.current > DRAG_CLICK_THRESHOLD) {
      e.stopPropagation();
      e.preventDefault();
    }
  }, []);

  // ─── arrow / dot handlers ──────────────────────────────────

  const onArrowClick = useCallback(
    (direction: 'left' | 'right') => {
      interactionPause();
      const ext = currentExtIndex();
      smoothScrollTo(direction === 'left' ? ext - 1 : ext + 1);
    },
    [interactionPause, currentExtIndex, smoothScrollTo],
  );

  const onDotClick = useCallback(
    (realIdx: number) => {
      interactionPause();
      const ext = currentExtIndex();
      const currentReal = toRealIndex(ext);

      // Find shortest path (wrapping)
      let diff = realIdx - currentReal;
      if (diff > TOTAL / 2) diff -= TOTAL;
      if (diff < -TOTAL / 2) diff += TOTAL;

      smoothScrollTo(ext + diff);
    },
    [interactionPause, currentExtIndex, smoothScrollTo],
  );

  const onCardClick = useCallback(
    (extIdx: number) => {
      if (dragDistance.current > DRAG_CLICK_THRESHOLD) return;
      interactionPause();
      smoothScrollTo(extIdx);
    },
    [interactionPause, smoothScrollTo],
  );

  // ─── render ────────────────────────────────────────────────

  return (
    <section className={cn('w-full', className)}>
      {/* Hero Header */}
      <div className="text-center mb-6 md:mb-8 max-w-4xl mx-auto px-4">
        <h1 className="gradient-text text-2xl md:text-4xl lg:text-[2.75rem] font-bold leading-tight whitespace-pre-line mt-6 mb-4 md:mt-10 md:mb-6">
          {t('welcome.heroTitle')}
        </h1>
        <p className="text-[var(--color-charcoal)]/70 text-sm md:text-lg leading-relaxed max-w-3xl mx-auto mb-6 md:mb-10">
          {t('welcome.heroSubtitle')}
        </p>

        <Button
          size="lg"
          data-track="dashboard_new_analysis_top"
          className="btn-primary px-8 py-3 text-base rounded-full"
          onClick={openAnalysisDialog}
        >
          {t('welcome.ctaButtonTop')}
        </Button>
      </div>

      {/* Carousel Section */}
      <div className="relative mt-4 md:mt-8 mb-8 -mx-4 md:-mx-8 lg:-mx-12">
        {/* Section header — layered above gradient */}
        <div className="relative z-10 text-center pt-6 md:pt-14 px-4">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--color-primary-start)] mb-3">
            {t('welcome.processLabel')}
          </p>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[var(--color-charcoal)]">
            {t('welcome.processHeadline')}
          </h2>
        </div>

        {/* Gradient bg + cards */}
        <div
          className="carousel-gradient-bg absolute inset-0"
          aria-hidden="true"
        />
        <div
          className="relative z-10 pt-6 md:pt-14 pb-10 md:pb-24 px-4 md:px-8 lg:px-12"
          onMouseEnter={pauseAutoPlay}
          onMouseLeave={scheduleResume}
        >

        <div className="relative max-w-5xl mx-auto">
          {/* Left arrow — hidden on mobile, visible on md+ */}
          <button
            onClick={() => onArrowClick('left')}
            className={cn(
              'absolute -left-3 md:-left-12 top-1/2 -translate-y-1/2 z-10 hidden md:flex',
              'w-9 h-9 rounded-full items-center justify-center',
              'bg-white/80 backdrop-blur-sm border border-gray-200/60',
              'shadow-sm hover:shadow-md hover:bg-white transition-all duration-200',
            )}
            aria-label={t('welcome.scrollLeft')}
          >
            <ChevronLeft className="w-4 h-4 text-[var(--color-charcoal)]/70" />
          </button>

          {/* Right arrow — hidden on mobile, visible on md+ */}
          <button
            onClick={() => onArrowClick('right')}
            className={cn(
              'absolute -right-3 md:-right-12 top-1/2 -translate-y-1/2 z-10 hidden md:flex',
              'w-9 h-9 rounded-full items-center justify-center',
              'bg-white/80 backdrop-blur-sm border border-gray-200/60',
              'shadow-sm hover:shadow-md hover:bg-white transition-all duration-200',
            )}
            aria-label={t('welcome.scrollRight')}
          >
            <ChevronRight className="w-4 h-4 text-[var(--color-charcoal)]/70" />
          </button>

          {/* Scrollable card track */}
          <div
            ref={trackRef}
            className={cn(
              'flex gap-5 overflow-x-auto pb-2 carousel-hide-scrollbar select-none snap-x snap-mandatory',
              isDragCursor ? 'cursor-grabbing' : 'cursor-grab',
            )}
            role="list"
            aria-label={t('welcome.processLabel')}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            onClickCapture={onClickCapture}
          >
            {extendedCards.map((card, extIdx) => {
              const isActive = card.realIndex === activeIndex;
              return (
                <article
                  key={extIdx}
                  data-carousel-card
                  aria-hidden={extIdx < CLONES || extIdx >= CLONES + TOTAL ? true : undefined}
                  onClick={() => onCardClick(extIdx)}
                  className={cn(
                    'flex-shrink-0 relative snap-center cursor-pointer',
                    'w-[280px] md:w-[calc(33.333%-14px)]',
                    'rounded-2xl p-6 md:p-7',
                    'transition-all duration-300 ease-out',
                    isActive
                      ? 'bg-white shadow-lg shadow-[var(--color-primary-start)]/8 border border-[var(--color-primary-start)]/15 scale-[1.02]'
                      : 'bg-white/60 shadow-sm border border-gray-100 scale-100 opacity-75',
                  )}
                  role="listitem"
                >
                  {/* Step number + icon row */}
                  <div className="flex items-center gap-3 mb-4">
                    <div className={cn(
                      'w-10 h-10 rounded-xl flex items-center justify-center shrink-0 transition-all duration-300',
                      isActive
                        ? 'gradient-bg text-white shadow-md shadow-[var(--color-primary-start)]/25'
                        : 'bg-gray-100 text-[var(--color-charcoal)]/40',
                    )}>
                      {card.icon}
                    </div>
                    <span className={cn(
                      'text-[11px] font-bold uppercase tracking-[0.15em] transition-colors duration-300',
                      isActive ? 'gradient-text' : 'text-[var(--color-charcoal)]/30',
                    )}>
                      {t('welcome.stepLabel', { number: card.realIndex + 1 })}
                    </span>
                  </div>

                  <h3 className="text-base font-bold text-[var(--color-charcoal)] mb-2 leading-snug">
                    {t(card.titleKey)}
                  </h3>

                  <p className="text-[13px] text-[var(--color-charcoal)]/55 leading-relaxed">
                    {t(card.descriptionKey)}
                  </p>

                </article>
              );
            })}
          </div>

          {/* Dot indicators */}
          <div className="flex justify-center items-center gap-2 mt-8" role="tablist" aria-label={t('welcome.processLabel')}>
            {processSteps.map((_, index) => (
              <button
                key={index}
                onClick={() => onDotClick(index)}
                className={cn(
                  'rounded-full transition-all duration-300',
                  index === activeIndex
                    ? 'w-6 h-2 gradient-bg'
                    : 'w-2 h-2 bg-[var(--color-charcoal)]/15 hover:bg-[var(--color-charcoal)]/30',
                )}
                role="tab"
                aria-selected={index === activeIndex}
                aria-label={t('welcome.stepLabel', { number: index + 1 })}
              />
            ))}
          </div>
        </div>
      </div>
      </div>

      {/* Bottom CTA Button */}
      <div className="text-center mb-4">
        <Button
          size="lg"
          data-track="dashboard_new_analysis_bottom"
          className="btn-primary px-8 py-3 text-base rounded-full"
          onClick={openAnalysisDialog}
        >
          {t('welcome.ctaButtonBottom')}
        </Button>
      </div>
    </section>
  );
}
